// The Library at laptop width, exactly as Main.dc.html: the date and greeting, Adam's announcement,
// Next up, Coming up, the tiles in each product's look, Free webinars and Free resources. Free
// webinars is Adam's of 9 Oct, in place of the board's 3 cards (Webinars.jsx).
// Coming up's Join and Add to calendar are design 5.3's and the sign-off's; the board draws neither.
// After the hub's own In progress tiles come the portal cards (Adam, 10 Oct): the Accelerator and POYT
// as the board draws their tiles, each opening its own site in a new tab.
// Next up has Join too, from 15 minutes before its session until the end, and the Coming up card
// for that same session leaves out Add to calendar, which Next up already has (Adam, 3 Oct).
import { Link } from '../lib/router.jsx'
import { readOnlyControl } from '../lib/viewAs.jsx'
import { TileArt } from './Art.jsx'
import { Arrow, CloseIcon } from '../shell/Marks.jsx'
import { OutArrow } from '../free/Icons.jsx'
import { FreeGlyph } from './FreeGlyph.jsx'
import { useCovers } from '../free/covers.js'
import { DateBlock } from './DateBlock.jsx'
import { LibraryWebinars } from './Webinars.jsx'

export const calLabel = added => (added ? 'Added to your calendar' : 'Add to calendar')

// Join opens the session's Zoom link in a new tab; Add to calendar downloads it as an .ics file
// (none on the card for Next up's session: its calendar is null)
export function SessionActions({ session, added, onAddCal, className }) {
  // the visible words first, then which session, so each button's name says what it's for
  const what = `${session.product}, ${session.title}`
  if (!session.join && !session.calendar) return null
  return (
    <div className={className}>
      {session.join && (
        <a href={session.join} target="_blank" rel="noopener noreferrer" className="session-btn session-btn--ink"
          aria-label={`Join: ${what}`}>Join</a>
      )}
      {session.calendar && (
        <button type="button" className="session-btn" onClick={() => onAddCal(session.calendar)} aria-label={`${calLabel(added)}: ${what}`}>
          {calLabel(added)}
        </button>
      )}
    </div>
  )
}

function Tile({ tile }) {
  const inside = (
    <>
      <TileArt tile={tile} />
      <div className="tile-body">
        <div className="tile-kind">{tile.kind}</div>
        <div className="tile-name">{tile.name}</div>
        {tile.status && tile.level && (
          // where they stand, then the status, on 1 line: "Behind. Next session Sun 25 Oct, 5pm" (levels.js)
          <div className="tile-line">
            <span className={`tile-level tile-level--${tile.level.key}`}>{`${tile.level.word}.`}</span>{' '}
            <span className="tile-status">{tile.status}</span>
          </div>
        )}
        {tile.status && !tile.level && (
          <div className={tile.strong ? 'tile-status tile-status--strong' : tile.external ? 'tile-status tile-status--out' : 'tile-status'}>
            {tile.status}{tile.external && <OutArrow size={14} />}
          </div>
        )}
        {tile.pct != null && <div className="bar"><div className="bar-fill" style={{ width: `${tile.pct}%` }} /></div>}
        {tile.pctLabel && <div className="tile-pct">{tile.pctLabel}</div>}
        {tile.external && <span className="sr-only"> (opens in a new tab)</span>}
      </div>
    </>
  )
  // a portal card: the product's own site, in a new tab, where they log in as they do today (Adam, 10 Oct)
  if (tile.external) return <a href={tile.href} target="_blank" rel="noopener noreferrer" className="ptile">{inside}</a>
  return <Link to={tile.href} className="ptile">{inside}</Link>
}

export default function LibraryLaptop({ model, onHide, onAddCal, added, viewingAs, note }) {
  const m = model
  const covers = useCovers(m.freebies)
  return (
    <main className="lib-main">
      {note}
      <section className="lib-hello">
        <div className="lib-date">{m.dateLine}</div>
        <h1 className="lib-h1">{m.greeting}</h1>
      </section>

      {m.announcement && (
        <section aria-label="Announcement" className="lib-news">
          <div className="lib-news-text">
            <span className="lib-news-from">{m.announcement.from}</span>
            <span className="lib-news-head">{m.announcement.headline}</span>
            {m.announcement.line && <span className="lib-news-line">{m.announcement.line}</span>}
          </div>
          <div className="lib-news-actions">
            {m.announcement.button && <Link to={m.announcement.button.href} className="lib-news-btn">{m.announcement.button.label}</Link>}
            <button type="button" className="lib-news-hide" aria-label="Hide this announcement" onClick={() => onHide(m.announcement.id)} {...readOnlyControl(viewingAs)}><CloseIcon /></button>
          </div>
        </section>
      )}

      {m.nextUp && (
        <section className="lib-next">
          <div className="lib-next-text">
            <div className="lib-next-eyebrow"><span className={`dot dot--${m.nextUp.accent}`} />Next up</div>
            <div className="lib-next-title">{m.nextUp.title}</div>
            <div className="lib-next-line">{m.nextUp.line}</div>
          </div>
          <div className="lib-next-actions">
            {m.nextUp.calendar && (
              <button type="button" className="btn btn-line" onClick={() => onAddCal(m.nextUp.calendar)}>
                {calLabel(added.has(m.nextUp.calendar.uid))}
              </button>
            )}
            {/* while Join shows, it's the 1 black button */}
            <Link to={m.nextUp.button.href} className={m.nextUp.join ? 'btn btn-line' : 'btn btn-ink'}>{m.nextUp.button.label}</Link>
            {m.nextUp.join && (
              <a href={m.nextUp.join.href} target="_blank" rel="noopener noreferrer" className="btn btn-ink" aria-label={m.nextUp.join.label}>Join</a>
            )}
          </div>
        </section>
      )}

      {m.comingUp.length > 0 && (
        <section className="lib-section">
          <h2 className="lib-h2">Coming up</h2>
          <div className="grid grid--4">
            {m.comingUp.map(s => (
              <div key={s.id} className="session">
                <div className="session-top">
                  <DateBlock block={s.block} />
                  <div className="session-text">
                    <div className="session-product"><span className={`dot dot--ring dot--${s.accent}`} />{s.product}</div>
                    <div className="session-title">{s.title}</div>
                    <div className="session-time">{s.time}</div>
                  </div>
                </div>
                <SessionActions session={s} added={added.has(s.id)} onAddCal={onAddCal} className="session-actions" />
              </div>
            ))}
          </div>
        </section>
      )}

      {m.inProgress.length > 0 && (
        <section className="lib-section">
          <h2 className="lib-h2">In progress</h2>
          <div className="grid grid--4">{m.inProgress.map(t => <Tile key={t.id} tile={t} />)}</div>
        </section>
      )}

      {m.startingSoon.length > 0 && (
        <section className="lib-section">
          <h2 className="lib-h2">Starting soon</h2>
          <div className="grid grid--4">{m.startingSoon.map(t => <Tile key={t.id} tile={t} />)}</div>
        </section>
      )}
      {m.joinLine && <p className={m.inProgress.length + m.startingSoon.length ? 'lib-join' : 'lib-join lib-join--alone'}>Everything you join shows up here.</p>}

      {/* a product whose access has ended, while it keeps yours-for-life items (Adam, 3 Oct; no board) */}
      {m.forLife.length > 0 && (
        <section className="lib-section" aria-label="Yours for life">
          <h2 className="lib-h2">Yours for life</h2>
          <div className="grid grid--4">{m.forLife.map(t => <Tile key={t.id} tile={t} />)}</div>
        </section>
      )}

      <LibraryWebinars webinars={m.webinars} />

      {m.freebies.length > 0 && (
        <section className="lib-section">
          <div className="lib-section-head">
            <h2 className="lib-h2">Free resources</h2>
            <Link to="/free-resources" className="lib-all">All free resources</Link>
          </div>
          <div className="grid grid--3">
            {m.freebies.map(fr => (
              <Link key={fr.id} to="/free-resources" className="fcard">
                <FreeGlyph item={fr} covers={covers} />
                <span className="free-text">
                  <span className="tile-kind">{fr.kind}</span>
                  <span className="free-name">{fr.name}</span>
                  <span className="free-action">{fr.action}<Arrow /></span>
                </span>
              </Link>
            ))}
          </div>
        </section>
      )}
    </main>
  )
}

export { DateBlock }
